/**
 * ITORA API service for communicating with the ComfyUI server
 */

// Use relative paths — Vite proxies /itora* → localhost:3003

/**
 * Check server status
 */
export const checkServerStatus = async () => {
  try {
    const response = await fetch(`/itoraStatus`);
    if (response.ok) {
      return { status: 'connected' };
    } else {
      return { status: 'disconnected', error: 'Server unavailable' };
    }
  } catch (error) {
    return { status: 'error', error: error.message };
  }
};

/**
 * Load available workflows
 */
export const loadAvailableWorkflows = async () => {
  try {
    const response = await fetch(`/itoraWorkflows`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Failed to load workflows');
    }
  } catch (error) {
    throw new Error(`Error loading workflows: ${error.message}`);
  }
};

/**
 * Upload an image to the server
 */
export const uploadImage = async (file, nodeId) => {
  try {
    const toBase64 = (f) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

    const image = await toBase64(file);
    console.log('Frontend: Uploading file:', file.name, 'Size:', file.size);

    const response = await fetch(`/itoraUpload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image, filename: file.name, nodeId })
    });

    console.log('Frontend: Upload response status:', response.status);

    if (response.ok) {
      const result = await response.json();
      console.log('Frontend: Upload result:', result);
      return result;
    } else {
      const errorText = await response.text();
      console.log('Frontend: Upload error response:', errorText);
      throw new Error('Upload failed');
    }
  } catch (error) {
    console.log('Frontend: Upload catch error:', error);
    throw new Error(`Upload error: ${error.message}`);
  }
};

/**
 * Execute a workflow
 */
export const executeWorkflow = async (workflow) => {
  try {
    const response = await fetch(`/itoraExecute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ workflow })
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Workflow execution failed');
    }
  } catch (error) {
    throw new Error(`Execution error: ${error.message}`);
  }
};

/**
 * Check workflow status
 */
export const checkWorkflowStatus = async (promptId) => {
  try {
    const response = await fetch(`/itoraWorkflowStatus/${promptId}`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Status check failed');
    }
  } catch (error) {
    throw new Error(`Status check error: ${error.message}`);
  }
};

/**
 * Fetch workflow results
 */
export const fetchWorkflowResults = async (promptId) => {
  try {
    const response = await fetch(`/itoraResults/${promptId}`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Failed to fetch results');
    }
  } catch (error) {
    throw new Error(`Results fetch error: ${error.message}`);
  }
};

/**
 * Fetch workflow history (might contain node-to-result mapping)
 */
export const fetchWorkflowHistory = async (promptId) => {
  try {
    const response = await fetch(`/itoraHistory/${promptId}`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Failed to fetch history');
    }
  } catch (error) {
    throw new Error(`History fetch error: ${error.message}`);
  }
};

/**
 * Get queue status
 */
export const getQueueStatus = async () => {
  try {
    const response = await fetch(`/itoraQueue`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Failed to get queue status');
    }
  } catch (error) {
    throw new Error(`Queue status error: ${error.message}`);
  }
};

/**
 * Get history
 */
export const getHistory = async () => {
  try {
    const response = await fetch(`/itoraHistory`);
    if (response.ok) {
      return await response.json();
    } else {
      throw new Error('Failed to get history');
    }
  } catch (error) {
    throw new Error(`History error: ${error.message}`);
  }
};
